import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, Video, Book, Users, Check, GraduationCap, Globe, Sparkles, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PROGRAM_IMAGES = {
  k5: 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/yxdn1mzg_iStock-1373258176.jpg',
  middle: 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/sbwj184j_iStock-2148802894.jpg',
  high: 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/zkqvcfhw_iStock-973279516.jpg',
};

export default function ProgramsPage() {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const isBridgeOnly = searchParams.get('track') === 'bridge';
  const [activeTrack, setActiveTrack] = useState(
    isBridgeOnly ? 'nonAnglophone' : 'anglophone'
  );

  useEffect(() => {
    if (searchParams.get('track') === 'bridge') {
      setActiveTrack('nonAnglophone');
    }
  }, [searchParams]);

  const tracks = {
    anglophone: language === 'fr' ? {
      label: 'OFA Fluent Track',
      tagline: 'Niveau avancé en anglais',
      desc: 'Pour les élèves ayant un niveau avancé en anglais. Un parcours académique rigoureux, identique à celui suivi dans les écoles américaines.',
      diploma: 'Bilingual High School Diploma',
      levels: [
        {
          id: 'k5',
          grades: 'K-5',
          title: 'Élémentaire (GS-CM2)',
          subtitle: 'Fondations Académiques',
          desc: 'Cours d\'anglais de niveau avancé : lecture, écriture créative, vocabulaire enrichi et exploration culturelle américaine. À partir du CM1, intégration des Social Studies (culture et société, US History, World History) dans les sessions.',
          features: ['Lecture et narration de niveau avancé', 'Écriture créative avancée', 'Vocabulaire et expression orale enrichis', 'Social Studies dès le CM1 (culture, histoire américaine et mondiale)'],
          color: '#66CA9D',
          image: PROGRAM_IMAGES.k5,
        },
        {
          id: 'middle',
          grades: '6-8',
          title: 'Collège (6e-4e)',
          subtitle: 'Accélération Académique',
          desc: 'Analyse littéraire, rédaction analytique et pensée critique au niveau des standards académiques américains. Social Studies intégrés : culture et société, US History, World History.',
          features: ['Analyse littéraire avancée', 'Rédaction analytique et argumentative', 'Social Studies : US History, World History, culture et société', 'Pensée critique et débat'],
          color: '#F8CD46',
          image: PROGRAM_IMAGES.middle,
        },
        {
          id: 'high',
          grades: '9-12',
          title: 'Lycée (3e-Terminale)',
          subtitle: 'Préparation Universitaire',
          desc: 'Cours avancés de niveau High School américain : littérature, composition, US History. Préparation au Bilingual High School Diploma.',
          features: ['Littérature et Composition avancées', 'US History (2 crédits - niveau avancé)', 'Préparation SAT/TOEFL', 'Accompagnement orientation universitaire'],
          color: '#2D78F6',
          image: PROGRAM_IMAGES.high,
        },
      ],
    } : {
      label: 'OFA Fluent Track',
      tagline: 'Advanced English level',
      desc: 'For students with an advanced level of English. A rigorous academic pathway, identical to what students follow in American schools.',
      diploma: 'Bilingual High School Diploma',
      levels: [
        {
          id: 'k5',
          grades: 'K-5',
          title: 'Elementary (K-5)',
          subtitle: 'Academic Foundations',
          desc: 'Advanced-level English courses: reading, creative writing, enriched vocabulary and American cultural exploration. From 4th grade, Social Studies (culture & society, US History, World History) integrated into sessions.',
          features: ['Advanced-level reading and storytelling', 'Advanced creative writing', 'Enriched vocabulary and oral expression', 'Social Studies from 4th grade (culture, American & world history)'],
          color: '#66CA9D',
          image: PROGRAM_IMAGES.k5,
        },
        {
          id: 'middle',
          grades: '6-8',
          title: 'Middle School (6-8)',
          subtitle: 'Academic Acceleration',
          desc: 'Literary analysis, analytical writing and critical thinking at American academic standards. Social Studies integrated: culture & society, US History, World History.',
          features: ['Advanced literary analysis', 'Analytical and argumentative writing', 'Social Studies: US History, World History, culture & society', 'Critical thinking and debate'],
          color: '#F8CD46',
          image: PROGRAM_IMAGES.middle,
        },
        {
          id: 'high',
          grades: '9-12',
          title: 'High School (9-12)',
          subtitle: 'University Preparation',
          desc: 'Advanced American High School level courses: literature, composition, US History. Preparation for the Bilingual High School Diploma.',
          features: ['Advanced Literature and Composition', 'US History (2 credits - advanced level)', 'SAT/TOEFL Preparation', 'College counseling support'],
          color: '#2D78F6',
          image: PROGRAM_IMAGES.high,
        },
      ],
    },
    nonAnglophone: language === 'fr' ? {
      label: 'OFA Bridge Track',
      tagline: 'Pour les familles qui souhaitent offrir une réelle maîtrise de l\'anglais',
      desc: 'Pour les élèves en cours d\'apprentissage de l\'anglais. Un parcours progressif pour atteindre le plus haut niveau de bilinguisme possible, avec un accompagnement adapté.',
      diploma: 'Bilingual High School Diploma',
      levels: [
        {
          id: 'k5',
          grades: 'K-5',
          title: 'Élémentaire (GS-CM2)',
          subtitle: 'Immersion Progressive',
          desc: 'Introduction à l\'anglais académique à travers des activités engageantes et adaptées au niveau de chaque élève. Les compléments de culture et d\'histoire pourront être intégrés en fonction du niveau des élèves.',
          features: ['Immersion progressive en anglais', 'Lecture et écriture adaptées au niveau', 'Enrichissement du vocabulaire par le jeu', 'Découverte de la culture anglophone'],
          color: '#66CA9D',
          image: PROGRAM_IMAGES.k5,
        },
        {
          id: 'middle',
          grades: '6-8',
          title: 'Collège (6e-4e)',
          subtitle: 'Progression vers le Bilinguisme',
          desc: 'Renforcement des compétences linguistiques avec des textes adaptés, de la rédaction guidée et un développement progressif de la pensée critique en anglais. Social Studies (culture, histoire) intégrés progressivement en fonction du niveau.',
          features: ['Compréhension et analyse de textes adaptés', 'Rédaction guidée et progressive', 'Social Studies intégrés selon le niveau de l\'élève', 'Développement de la pensée critique'],
          color: '#F8CD46',
          image: PROGRAM_IMAGES.middle,
        },
        {
          id: 'high',
          grades: '9-12',
          title: 'Lycée (3e-Terminale)',
          subtitle: 'Vers l\'Excellence Bilingue',
          desc: 'Cours de niveau adapté menant au Bilingual High School Diploma. US History avec un niveau d\'exigence linguistique ajusté.',
          features: ['Littérature et Composition (niveau adapté)', 'US History (2 crédits - niveau adapté)', 'Renforcement linguistique intensif', 'Accompagnement orientation universitaire'],
          color: '#2D78F6',
          image: PROGRAM_IMAGES.high,
        },
      ],
    } : {
      label: 'OFA Bridge Track',
      tagline: 'For families who want to offer true English mastery',
      desc: 'For students building their English skills. A progressive pathway to reach the highest level of bilingualism possible, with tailored support.',
      diploma: 'Bilingual High School Diploma',
      levels: [
        {
          id: 'k5',
          grades: 'K-5',
          title: 'Elementary (K-5)',
          subtitle: 'Progressive Immersion',
          desc: 'Introduction to academic English through engaging activities adapted to each student\'s level. Culture and history complements may be integrated based on student level.',
          features: ['Progressive English immersion', 'Level-adapted reading and writing', 'Vocabulary building through play', 'Anglophone culture discovery'],
          color: '#66CA9D',
          image: PROGRAM_IMAGES.k5,
        },
        {
          id: 'middle',
          grades: '6-8',
          title: 'Middle School (6-8)',
          subtitle: 'Pathway to Bilingualism',
          desc: 'Strengthening language skills with adapted texts, guided writing, and progressive development of critical thinking in English. Social Studies (culture, history) progressively integrated based on student level.',
          features: ['Adapted text comprehension and analysis', 'Guided and progressive writing', 'Social Studies integrated based on student level', 'Critical thinking development'],
          color: '#F8CD46',
          image: PROGRAM_IMAGES.middle,
        },
        {
          id: 'high',
          grades: '9-12',
          title: 'High School (9-12)',
          subtitle: 'Toward Bilingual Excellence',
          desc: 'Adapted-level courses leading to the Bilingual High School Diploma. US History with adjusted linguistic requirements.',
          features: ['Literature and Composition (adapted level)', 'US History (2 credits - adapted level)', 'Intensive language reinforcement', 'College counseling support'],
          color: '#2D78F6',
          image: PROGRAM_IMAGES.high,
        },
      ],
    },
  };

  const currentTrack = tracks[activeTrack];

  const upcomingCourses = language === 'fr' ? [
    { name: 'Mathematics', desc: 'Cours de mathématiques en anglais' },
    { name: 'Sciences', desc: 'Biologie, physique, chimie en anglais' },
    { name: 'Debate & Public Speaking', desc: 'Art oratoire et argumentation' },
    { name: 'Reading Lab', desc: 'Atelier de lecture approfondie' },
    { name: 'Academic Writing', desc: 'Rédaction académique avancée' },
  ] : [
    { name: 'Mathematics', desc: 'Mathematics courses in English' },
    { name: 'Sciences', desc: 'Natural and physical sciences' },
    { name: 'Debate & Public Speaking', desc: 'Public speaking and argumentation' },
    { name: 'Reading Lab', desc: 'Intensive reading workshop' },
    { name: 'Academic Writing', desc: 'Advanced academic writing' },
  ];

  const formatItems = [
    { icon: Video, title: t.programs.format.live, desc: t.programs.format.liveDesc },
    { icon: Book, title: t.programs.format.async, desc: t.programs.format.asyncDesc },
    { icon: Users, title: t.programs.format.small, desc: t.programs.format.smallDesc },
    {
      icon: FileText,
      title: language === 'fr' ? 'Suivi & Bulletins' : 'Progress Reports',
      desc: language === 'fr'
        ? 'Bulletins trimestriels (progress reports) et semestriels envoyés aux parents via notre propre SIS (Student Information System).'
        : 'Quarterly progress reports and semester report cards sent to parents through our own SIS (Student Information System).',
    },
  ];

  return (
    <div data-testid="programs-page" className="min-h-screen bg-white">
      <SEO path="/programs" />

      {/* Schema Course */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "OFA France — Programme Élémentaire (GS-CM2)",
          "description": "Programme américain pour les élèves de Grande Section au CM2. Cours d'anglais avancé : lecture, écriture, vocabulaire et découverte culturelle américaine.",
          "provider": { "@type": "Organization", "name": "OFA France", "url": "https://ofalycee.fr" },
          "educationalLevel": "Primary",
          "inLanguage": "en",
          "availableLanguage": ["en", "fr"],
          "courseMode": "online",
          "hasCourseInstance": { "@type": "CourseInstance", "courseMode": "online", "instructor": { "@type": "Organization", "name": "OFA France" } }
        },
        {
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "OFA France — Programme Collège (6e-4e)",
          "description": "Programme américain pour les collégiens. Analyse littéraire, rédaction, pensée critique et introduction à la littérature américaine.",
          "provider": { "@type": "Organization", "name": "OFA France", "url": "https://ofalycee.fr" },
          "educationalLevel": "Middle School",
          "inLanguage": "en",
          "availableLanguage": ["en", "fr"],
          "courseMode": "online"
        },
        {
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "OFA France — Programme Lycée (3e-Terminale)",
          "description": "Programme américain pour les lycéens menant au U.S. High School Diploma accrédité COGNIA. Littérature avancée, US History et préparation universitaire.",
          "provider": { "@type": "Organization", "name": "OFA France", "url": "https://ofalycee.fr" },
          "educationalLevel": "High School",
          "inLanguage": "en",
          "availableLanguage": ["en", "fr"],
          "courseMode": "online",
          "educationalCredentialAwarded": "U.S. High School Diploma"
        }
      ])}} />

      <Navigation />
      
      {/* Hero */}
      <section className={`pt-32 pb-20 ${isBridgeOnly ? 'bg-[#14274B]' : 'bg-[#2D78F6]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isBridgeOnly && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-4 mb-6"
            >
              <div className="w-14 h-14 bg-[#66CA9D]/20 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-[#66CA9D]" />
              </div>
            </motion.div>
          )}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`text-4xl sm:text-5xl lg:text-6xl font-light leading-tight tracking-tight mb-6 text-white`}
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {isBridgeOnly
              ? (language === 'fr' ? 'Devenir Bilingue' : 'Become Bilingual')
              : t.programs.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`text-lg max-w-3xl ${isBridgeOnly ? 'text-white/70' : 'text-white/80'}`}
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {isBridgeOnly
              ? (language === 'fr'
                ? 'Pour les familles en France souhaitant offrir à leur enfant un parcours progressif vers la maîtrise de l\'anglais. Un accompagnement adapté, de la Grande Section à la Terminale.'
                : 'For families in France who want to give their child a progressive pathway to English mastery. Tailored support, from Kindergarten to 12th grade.')
              : t.programs.heroSubtitle}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Intro SEO */}
      <section className="py-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#14274B] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'OFA France propose un programme scolaire américain complet, de la Grande Section à la Terminale, en parallèle de l\'école française. Nos cours sont dispensés en direct par des enseignants américains certifiés, dans des classes de 4 à 8 élèves maximum. Ce programme accrédité par COGNIA permet à votre enfant d\'obtenir un U.S. High School Diploma reconnu dans le monde entier, tout en poursuivant sa scolarité française.'
              : 'OFA France offers a complete American academic program, from Kindergarten to 12th grade, alongside French schooling. Our classes are taught live by certified American teachers, in groups of 4 to 8 students maximum. This COGNIA-accredited program allows your child to earn a U.S. High School Diploma recognized worldwide, while continuing their French education.'}
          </p>
          <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Deux parcours sont proposés : l\'OFA Fluent Track pour les élèves déjà bilingues ou de retour d\'expatriation, et l\'OFA Bridge Track pour ceux qui souhaitent acquérir un niveau avancé en anglais. Chaque parcours est adapté au niveau et aux objectifs de l\'enfant, avec un suivi individualisé tout au long de l\'année.'
              : 'Two tracks are offered: the OFA Fluent Track for students who are already bilingual or returning from expatriation, and the OFA Bridge Track for those who wish to achieve an advanced level of English. Each track is adapted to the child\'s level and goals, with individualized monitoring throughout the year.'}
          </p>
        </div>
      </section>

      {/* Les 3 niveaux - shown on main programs page before tracks */}
      {!isBridgeOnly && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-[#14274B] mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Un parcours complet' : 'A complete pathway'}
          </h2>
          <p className="text-[#475569] mb-10 max-w-2xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Des cours d\'anglais de la Grande Section à la Terminale, en complément de la scolarité française.'
              : 'English courses from Kindergarten to 12th grade, complementing French schooling.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(language === 'fr' ? [
              { grades: 'GS-CM2', title: 'Élémentaire', color: '#66CA9D', desc: 'Construction des fondations en anglais : lecture, écriture, vocabulaire et découverte culturelle adaptés à l\'âge de l\'enfant.', image: PROGRAM_IMAGES.k5 },
              { grades: '6e-4e', title: 'Collège', color: '#F8CD46', desc: 'Approfondissement académique : analyse littéraire, rédaction, pensée critique et introduction à la littérature américaine.', image: PROGRAM_IMAGES.middle },
              { grades: '3e-Term.', title: 'Lycée', color: '#2D78F6', desc: 'Préparation universitaire : littérature avancée, US History, et préparation au Bilingual High School Diploma.', image: PROGRAM_IMAGES.high },
            ] : [
              { grades: 'K-5', title: 'Elementary', color: '#66CA9D', desc: 'Building English foundations: reading, writing, vocabulary and age-appropriate cultural discovery.', image: PROGRAM_IMAGES.k5 },
              { grades: '6-8', title: 'Middle School', color: '#F8CD46', desc: 'Academic deepening: literary analysis, writing, critical thinking and introduction to American literature.', image: PROGRAM_IMAGES.middle },
              { grades: '9-12', title: 'High School', color: '#2D78F6', desc: 'University preparation: advanced literature, US History, and Bilingual High School Diploma preparation.', image: PROGRAM_IMAGES.high },
            ]).map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                className="overflow-hidden border border-[#E7EAEF]"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={level.image} alt={level.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div
                    className="inline-block px-3 py-1 mb-3 text-xs font-medium text-white"
                    style={{ backgroundColor: level.color, fontFamily: 'Poppins, sans-serif' }}
                  >
                    {level.grades}
                  </div>
                  <h3 className="text-xl font-semibold text-[#14274B] mb-2" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                    {level.title}
                  </h3>
                  <p className="text-sm text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {level.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Track Selector - only shown on main programs page */}
      {!isBridgeOnly && (
      <section className="py-12 bg-white border-b border-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl md:text-3xl font-light text-[#14274B] mb-3 text-center"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Deux voies, un même objectif : le bilinguisme' : 'Two tracks, one goal: bilingualism'}
          </h2>
          <p
            className="text-center text-[#475569] mb-3 max-w-2xl mx-auto"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'Chaque élève arrive avec un niveau d\'anglais différent. OFA France propose deux parcours adaptés pour que chacun progresse à son rythme vers l\'excellence bilingue.'
              : 'Every student arrives with a different English level. OFA France offers two adapted tracks so each student can progress at their own pace toward bilingual excellence.'}
          </p>
          <p
            className="text-center text-[#475569] mb-8 text-sm"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr' ? 'Choisissez le parcours adapté au profil de votre enfant' : 'Choose the track that fits your child\'s profile'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 max-w-3xl mx-auto">
            <Link
              to="/returning-families"
              data-testid="track-anglophone"
              className="flex-1 px-6 py-5 border-2 border-[#2D78F6] bg-[#2D78F6]/5 transition-all duration-300 text-left hover:bg-[#2D78F6]/10"
            >
              <div className="flex items-center gap-3 mb-2">
                <Globe className="w-5 h-5 text-[#2D78F6]" />
                <span
                  className="font-semibold text-lg text-[#14274B]"
                  style={{ fontFamily: 'Gabarito, sans-serif' }}
                >
                  {language === 'fr' ? 'OFA Fluent Track / Rester Bilingue' : 'OFA Fluent Track / Stay Bilingual'}
                </span>
              </div>
              <p className="text-sm text-[#475569] ml-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Niveau avancé en anglais' : 'Advanced English level'}
              </p>
            </Link>

            <Link
              to="/returning-families"
              data-testid="track-non-anglophone"
              className="flex-1 px-6 py-5 border-2 border-[#66CA9D] bg-[#66CA9D]/5 transition-all duration-300 text-left hover:bg-[#66CA9D]/10"
            >
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-5 h-5 text-[#66CA9D]" />
                <span
                  className="font-semibold text-lg text-[#14274B]"
                  style={{ fontFamily: 'Gabarito, sans-serif' }}
                >
                  {language === 'fr' ? 'OFA Bridge Track / Devenir Bilingue' : 'OFA Bridge Track / Become Bilingual'}
                </span>
              </div>
              <p className="text-sm text-[#475569] ml-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Pour les familles qui souhaitent offrir une réelle maîtrise de l\'anglais' : 'For families who want to offer true English mastery'}
              </p>
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Upcoming Courses */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2
              className="text-3xl md:text-4xl font-light text-[#14274B] mb-3"
              style={{ fontFamily: 'Gabarito, sans-serif' }}
            >
              {language === 'fr' ? 'Cours Optionnels (en préparation)' : 'Optional Courses (coming soon)'}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {upcomingCourses.map((course, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.05 * index }}
                className="bg-white p-5 border border-[#E7EAEF] hover:border-[#2D78F6]/30 transition-colors"
              >
                <h3 className="font-semibold text-[#14274B] mb-1" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {course.name}
                </h3>
                <p className="text-sm text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {course.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Format Section */}
      <section className="py-20 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-12"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.programs.format.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {formatItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-white/5 p-8"
              >
                <item.icon className="w-10 h-10 text-[#2D78F6] mb-4" />
                <h3
                  className="text-xl font-semibold text-white mb-2"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {item.title}
                </h3>
                <p className="text-white/60" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.cta.title}
          </h2>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="programs-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.cta.primary}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
